import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import moment from 'moment'

import { useMembershipRedux } from '@hooks'
import { ROUTES, OTP_EXPIRY_TIME, customToast } from '@common'
import { otpSchema, checkResendLimit, updateResendState, extractErrorMessage, decryptPassword } from '@utils'
import { ErrorTrackingAPI } from '@services'

const EmailVerificationPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const {
        emailVerificationData,
        setEmailVerified,
        setError: setReduxError,
        clearError,
        isLoading,
        error,
        verifyEmailOTP,
        getMemberAccessToken,
        getCurrentMember,
        requireOTP,
        setEmailVerificationData,
    } = useMembershipRedux()

    const { email, isRegistration, password, otpExpiry, nextAvailableTime } = emailVerificationData

    const [otp, setOtp] = useState(['', '', '', '', '', ''])
    const [isVerifying, setIsVerifying] = useState(false)
    const [timeLeft, setTimeLeft] = useState(0)
    const [canResend, setCanResend] = useState(false)
    const [resendLimit, setResendLimit] = useState({ canResend: true, localLimitEnd: null })
    const hasAutoRequestedOTP = useRef(false)
    const timerRef = useRef(null)
    // Use ref to store latest values for timer closure
    const latestValuesRef = useRef({ email, nextAvailableTime, otpExpiry })

    // Update ref when values change
    useEffect(() => {
        latestValuesRef.current = { email, nextAvailableTime, otpExpiry }
    }, [email, nextAvailableTime, otpExpiry])

    // Check if should auto-request OTP from navigation state
    const shouldAutoRequestOTP = location.state?.shouldAutoRequestOTP || false
    // React Hook Form setup with Yup validation
    const {
        handleSubmit,
        formState: { errors },
        setValue,
        clearErrors,
    } = useForm({
        resolver: yupResolver(otpSchema),
        defaultValues: {
            otp: '',
        },
    })

    // Handle auto-request OTP when navigating from login page
    const handleAutoRequestOTP = useCallback(async () => {
        hasAutoRequestedOTP.current = true

        // Check resend limit before auto-requesting
        const limit = checkResendLimit(email)
        if (!limit.canResend) {
            // In local limit period, set countdown and clear navigation state
            if (location.state?.shouldAutoRequestOTP) {
                navigate(location.pathname, { replace: true, state: {} })
            }
            const state = updateResendState(email, nextAvailableTime, otpExpiry)
            setResendLimit(state.limit)
            setCanResend(state.canResend)
            setTimeLeft(state.timeLeft)
            return
        }

        // Auto-request OTP
        try {
            const result = await requireOTP({ email })

            // Clear the navigation state after processing
            if (location.state?.shouldAutoRequestOTP) {
                navigate(location.pathname, { replace: true, state: {} })
            }

            if (result.type.endsWith('/fulfilled')) {
                // Update OTP expiry in Redux (nextAvailableTime is automatically updated by Redux slice)
                const newOtpExpiry = Date.now() + OTP_EXPIRY_TIME * 1000
                setEmailVerificationData({
                    otpExpiry: newOtpExpiry,
                })

                // Update state after successful request
                const state = updateResendState(email, nextAvailableTime, newOtpExpiry)
                setResendLimit(state.limit)
                setCanResend(state.canResend)
                setTimeLeft(state.timeLeft)

                customToast.success('Verification code sent to your email', { duration: 3000 })
            } else {
                // If auto-request failed, reset the flag so user can manually retry
                hasAutoRequestedOTP.current = false
                const errorMessage = extractErrorMessage(result.payload, 'Failed to send verification code. Please try again.')
                setReduxError(errorMessage)
            }
        } catch (err) {
            // If auto-request failed, reset the flag so user can manually retry
            hasAutoRequestedOTP.current = false
            ErrorTrackingAPI.sendError(err, 'EmailVerificationPage', 'autoRequestOTP')
            setReduxError('Failed to send verification code. Please try again.')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, nextAvailableTime, otpExpiry, location, navigate])

    useEffect(() => {
        if (!email) {
            navigate(ROUTES.Membership.auth, { replace: true })
            return
        }

        // Auto-request OTP when navigating from login page with shouldAutoRequestOTP flag
        if (shouldAutoRequestOTP && !hasAutoRequestedOTP.current) {
            handleAutoRequestOTP()
            return
        }

        // Check if OTP is expired
        if (otpExpiry && Date.now() > otpExpiry) {
            setReduxError('OTP has expired. Please request a new one.')
            setCanResend(true)
            return
        }

        // Initialize countdown state
        const state = updateResendState(email, nextAvailableTime, otpExpiry)
        setResendLimit(state.limit)
        setCanResend(state.canResend)
        setTimeLeft(state.timeLeft)

        // Clear existing timer before creating a new one
        if (timerRef.current) {
            clearInterval(timerRef.current)
        }

        // Start countdown timer
        timerRef.current = setInterval(() => {
            // Always recalculate timeLeft to handle nextAvailableTime correctly
            // Use ref to get latest values to avoid closure issues
            const {
                email: latestEmail,
                nextAvailableTime: latestNextAvailableTime,
                otpExpiry: latestOtpExpiry,
            } = latestValuesRef.current
            const updatedState = updateResendState(latestEmail, latestNextAvailableTime, latestOtpExpiry)
            setResendLimit(updatedState.limit)
            setCanResend(updatedState.canResend)
            setTimeLeft(updatedState.timeLeft)
        }, 1000)

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, navigate, otpExpiry, isRegistration, nextAvailableTime])

    // Update form value when OTP array changes
    useEffect(() => {
        const otpString = otp.join('')
        setValue('otp', otpString)
        if (otpString.length === 6) {
            clearErrors('otp')
        }
    }, [otp, setValue, clearErrors])

    const handleOtpChange = (index, value) => {
        if (value.length > 1) return // Prevent multiple characters

        const newOtp = [...otp]
        newOtp[index] = value
        setOtp(newOtp)

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`)
            if (nextInput) nextInput.focus()
        }
    }

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`)
            if (prevInput) prevInput.focus()
        }
    }

    const handlePaste = e => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')
        if (pastedData.length === 6) {
            const newOtp = pastedData.split('')
            setOtp(newOtp)
        }
    }

    const onSubmit = async data => {
        setIsVerifying(true)
        clearError()

        try {
            const userEnteredOtp = otp.join('')

            // Check if OTP is expired using Redux otpExpiry
            if (otpExpiry && Date.now() > otpExpiry) {
                setReduxError('OTP has expired. Please request a new one.')
                return
            }

            // Verify Email OTP
            const verifyResult = await verifyEmailOTP({
                email,
                otp: userEnteredOtp,
            })

            if (!verifyResult.type.endsWith('/fulfilled')) {
                // Handle error
                const errorMessage = verifyResult.payload || 'Invalid OTP. Please check your code and try again.'
                setReduxError(errorMessage)
                return
            }

            // OTP verified successfully
            setEmailVerified(true)

            if (isRegistration && password) {
                // Registration flow: auto-login after verification
                // Decrypt password before using
                const decryptedPassword = await decryptPassword(password)
                if (!decryptedPassword) {
                    setReduxError('Failed to decrypt password. Please try logging in manually.')
                    setTimeout(() => {
                        navigate(ROUTES.Membership.auth)
                    }, 2000)
                    return
                }

                const loginResult = await getMemberAccessToken({
                    email,
                    password: decryptedPassword,
                    isRememberMe: false,
                })

                if (loginResult.type.endsWith('/fulfilled')) {
                    // Get member data using the access token
                    try {
                        const memberResult = await getCurrentMember()
                        if (!memberResult.type.endsWith('/fulfilled')) {
                            // Silently handle member data fetch failure - user is already logged in
                            // Error is already handled by Redux slice
                        }
                    } catch (error) {
                        // Silently handle member data fetch error - user is already logged in
                        // Error is already handled by Redux slice
                    }

                    // Clear encrypted password from Redux after successful login
                    setEmailVerificationData({
                        password: null,
                    })

                    customToast.success('Registration successful!', { duration: 2000 })
                    navigate(ROUTES.root)
                } else {
                    // Login failed after verification
                    const errorMessage = loginResult.payload || 'Failed to login. Please try logging in manually.'
                    setReduxError(errorMessage)
                    // Navigate to login page
                    setTimeout(() => {
                        navigate(ROUTES.Membership.auth)
                    }, 2000)
                }
            } else {
                // Resend verification flow: navigate to login page
                customToast.success('Email verified successfully!', { duration: 2000 })
                navigate(ROUTES.Membership.auth)
            }
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'EmailVerificationPage', 'handleSubmit')
            setReduxError('An unexpected error occurred. Please try again.')
        } finally {
            setIsVerifying(false)
        }
    }

    const handleResend = async () => {
        // Check current state
        const state = updateResendState(email, nextAvailableTime, otpExpiry)

        // Check if rate limited or in local limit
        if (!state.canResend) {
            const remaining = state.timeLeft
            const minutes = Math.floor(remaining / 60)
            const secs = remaining % 60
            setReduxError(`Please wait ${minutes}:${secs.toString().padStart(2, '0')} before requesting a new code.`)
            return
        }

        setCanResend(false)
        clearErrors('otp')
        clearError()

        try {
            const result = await requireOTP({ email })

            if (result.type.endsWith('/fulfilled')) {
                // Update OTP expiry in Redux (nextAvailableTime is automatically updated by Redux slice)
                const newOtpExpiry = Date.now() + OTP_EXPIRY_TIME * 1000
                setEmailVerificationData({
                    otpExpiry: newOtpExpiry,
                })

                // Update state after successful resend
                const updatedState = updateResendState(email, nextAvailableTime, newOtpExpiry)
                setResendLimit(updatedState.limit)
                setCanResend(updatedState.canResend)
                setTimeLeft(updatedState.timeLeft)

                customToast.success('Verification code sent to your email', { duration: 3000 })
                setOtp(['', '', '', '', '', ''])
            } else {
                // Handle error
                const errorMessage = extractErrorMessage(result.payload, 'Failed to resend verification code. Please try again.')
                setReduxError(errorMessage)
                setCanResend(true)
            }
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'EmailVerificationPage', 'handleResendOTP')
            setReduxError('An unexpected error occurred. Please try again.')
            setCanResend(true)
        }
    }

    const formatTime = seconds => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    return (
        <div className="otp-verification-page">
            <div className="otp-container">
                <div className="otp-header">
                    <h2>Verify Your Email</h2>
                    <p>We've sent a 6-digit code to</p>
                    <p className="verification-target">{email}</p>
                </div>

                {error && (
                    <div className="alert alert-danger">
                        <i className="bx bx-error-circle"></i>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="otp-inputs">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                id={`otp-${index}`}
                                type="number"
                                maxLength="1"
                                value={digit}
                                onChange={e => handleOtpChange(index, e.target.value)}
                                onKeyDown={e => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className={errors.otp ? 'error' : ''}
                            />
                        ))}
                    </div>

                    {errors.otp && <div className="alert alert-danger text-center mb-3">{errors.otp.message}</div>}

                    <button type="submit" disabled={isVerifying || isLoading || otp.join('').length !== 6} className="btn-verify">
                        {isVerifying || isLoading ? 'Verifying...' : 'Verify Code'}
                    </button>
                </form>

                <div className="resend-section">
                    {(() => {
                        // Check if nextAvailableTime is set and not yet reached (rate limited)
                        const isRateLimited = nextAvailableTime && moment(nextAvailableTime).isAfter(moment())
                        const shouldDisable = !canResend || !resendLimit.canResend || isRateLimited

                        if (shouldDisable) {
                            // Calculate time left: use nextAvailableTime if rate limited, otherwise use timeLeft
                            let displayTimeLeft = timeLeft
                            if (isRateLimited) {
                                const remaining = Math.max(
                                    0,
                                    Math.floor((moment(nextAvailableTime).valueOf() - moment().valueOf()) / 1000)
                                )
                                displayTimeLeft = remaining
                            }

                            return (
                                <div>
                                    <p className="countdown">Resend code in {formatTime(displayTimeLeft)}</p>
                                </div>
                            )
                        } else {
                            return (
                                <div>
                                    <button onClick={handleResend} className="resend-btn">
                                        Resend code
                                    </button>
                                </div>
                            )
                        }
                    })()}
                </div>

                <div className="back-link">
                    <button onClick={() => navigate(ROUTES.Membership.auth)}>← Change Email Address</button>
                </div>
            </div>
        </div>
    )
}

export default EmailVerificationPage
